import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { careServicesSidePanelMessages } from './CareServicesSidePanel.messages'
import { CareServiceProgram, CareServicesSidePanelData } from './types'
import { BodyText, Size as BodyTextSize } from '../../../atoms/bodyText/BodyText'
import { Avatar } from '../../../atoms/icons/Avatar'
import { MessageIcon } from '../../../atoms/icons/MessageIcon'
import { Link } from '../../../atoms/link/Link'
import { PressableOpacity } from '../../../atoms/pressableOpacity/PressableOpacity'
import { CareTeamMembersList } from '../../../molecules/careTeamProgramCard/CareTeamProgramCard'
import { tID } from '../../../utils/utils'
import { ClientDetailsSidePanelSection } from '../ClientDetailsSidePanelSection'

export type CareServicesSidePanelSectionProps = {
  data?: CareServicesSidePanelData
  onViewPastCare?: () => void
  onMessageProvider?: (providerId: string) => void
}

const AVATAR_SIZE = 40

const ProgramList = styled.View(({ theme }) => ({
  width: '100%',
  gap: theme.spacing['16px'],
}))

const CollapsedProgramList = styled.View(({ theme }) => ({
  width: '100%',
  gap: theme.spacing['8px'],
}))

const ProgramGroup = styled.View({
  width: '100%',
})

const ProgramHeader = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing['12px'],
}))

const CareTeamContainer = styled.View(({ theme }) => ({
  marginTop: theme.spacing['12px'],
}))

const ProviderRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing['12px'],
  marginTop: theme.spacing['12px'],
}))

const ProviderInfo = styled.View({
  flex: 1,
  minWidth: 0,
})

const MessageButton = styled(PressableOpacity)(({ theme }) => ({
  padding: theme.spacing['8px'],
}))

const ProgramGroupContent: FunctionComponent<{
  program: CareServiceProgram
  programIndex: number
  onMessageProvider?: (providerId: string) => void
}> = ({ program, programIndex, onMessageProvider }) => {
  const { colors } = useTheme()
  const { formatMessage } = useIntl()

  return (
    <ProgramGroup testID={tID(`CareServices-program-${programIndex}`)}>
      <ProgramHeader>
        <BodyText
          testID={tID(`CareServices-program-${programIndex}-name`)}
          size={BodyTextSize.SMALL}
          color={colors.textSecondary}
          text={program.programName}
          regular
        />
        {program.careTeam?.onManageTeamPress && (
          <Link
            testID={tID(`CareServices-program-${programIndex}-manage-team`)}
            size={BodyTextSize.SMALL}
            text={formatMessage(careServicesSidePanelMessages.manageTeam)}
            onPress={program.careTeam.onManageTeamPress}
          />
        )}
      </ProgramHeader>
      {program.careTeam && (
        <CareTeamContainer testID={tID(`CareServices-program-${programIndex}-care-team`)}>
          <CareTeamMembersList
            members={program.careTeam.members}
            unassigned={program.careTeam.unassigned}
            onMessageProvider={onMessageProvider}
          />
        </CareTeamContainer>
      )}
      {!program.careTeam &&
        program.providers.map((provider, providerIndex) => {
          const { providerId } = provider
          const roleLabel = provider.providerRole
            ? formatMessage(careServicesSidePanelMessages[provider.providerRole])
            : undefined

          return (
            <ProviderRow
              key={`provider-${providerIndex}`}
              testID={tID(`CareServices-program-${programIndex}-provider-${providerIndex}`)}
            >
              <Avatar
                details={{ src: provider.providerImageUrl ?? '', displayName: provider.providerName }}
                size={AVATAR_SIZE}
                accessibilityLabel={provider.providerName}
              />
              <ProviderInfo>
                <BodyText
                  testID={tID(`CareServices-program-${programIndex}-provider-${providerIndex}-name`)}
                  size={BodyTextSize.SMALL}
                  color={colors.textSecondary}
                  text={provider.providerName}
                />
                {roleLabel && (
                  <BodyText
                    testID={tID(`CareServices-program-${programIndex}-provider-${providerIndex}-role`)}
                    size={BodyTextSize.SMALL}
                    color={colors.textSecondary}
                    text={roleLabel}
                    regular
                  />
                )}
              </ProviderInfo>
              {provider.canMessage && providerId && onMessageProvider && (
                <MessageButton
                  testID={tID(`CareServices-message-${providerId}`)}
                  accessibilityRole='button'
                  accessibilityLabel={formatMessage(
                    { defaultMessage: 'Message {providerName}', description: 'Message a current care provider' },
                    { providerName: provider.providerName },
                  )}
                  onPress={() => onMessageProvider(providerId)}
                >
                  <MessageIcon size={24} />
                </MessageButton>
              )}
            </ProviderRow>
          )
        })}
    </ProgramGroup>
  )
}

export const CareServicesSidePanelSection: FunctionComponent<CareServicesSidePanelSectionProps> = ({
  data,
  onViewPastCare,
  onMessageProvider,
}) => {
  const { formatMessage } = useIntl()
  const { colors } = useTheme()
  const subtitle = formatMessage(careServicesSidePanelMessages.subtitle)

  const pastCareLink = onViewPastCare ? (
    <Link
      testID={tID('CareServices-viewPastCare')}
      text={formatMessage(careServicesSidePanelMessages.viewPastCare)}
      textAlign='center'
      onPress={onViewPastCare}
    />
  ) : undefined

  if (!data || data.programs.length === 0) {
    return pastCareLink ? (
      <ClientDetailsSidePanelSection
        key='past-care-only'
        subtitle={subtitle}
        accessibilityName={subtitle}
        header={{ kind: 'static' }}
        footer={pastCareLink}
        testIDPrefix='CareServices'
      />
    ) : null
  }

  const collapsedTitle =
    data.programs.length === 1 ? (
      data.programs[0].programName
    ) : (
      <CollapsedProgramList>
        {data.programs.map((program, index) => (
          <BodyText
            key={`${program.programName}-${index}`}
            testID={tID(`CareServices-collapsed-program-${index}`)}
            size={BodyTextSize.DEFAULT}
            color={colors.textPrimary}
            text={program.programName}
            regular
          />
        ))}
      </CollapsedProgramList>
    )

  const content = (
    <ProgramList>
      {data.programs.map((program, index) => (
        <ProgramGroupContent
          key={`program-${index}`}
          program={program}
          programIndex={index}
          onMessageProvider={onMessageProvider}
        />
      ))}
    </ProgramList>
  )

  return (
    <ClientDetailsSidePanelSection
      key='current-care'
      subtitle={subtitle}
      accessibilityName={subtitle}
      header={{
        kind: 'collapsible',
        collapsedTitle,
        collapsedTitleIsLayout: data.programs.length > 1,
      }}
      content={content}
      footer={pastCareLink}
      testIDPrefix='CareServices'
    />
  )
}
